import { useMemo, useState } from "react";
import { api, type CommandView } from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  RegisterLayout,
  Rail,
  Region,
  Entry,
  Apparatus,
  MarginNote,
  type RailKey,
} from "../components/Register";
import {
  AbsentNotice,
  Button,
  Failed,
  Figure,
  Mark,
  Progress,
  SkeletonRows,
  Empty,
  Field,
  TextArea,
  deliverableTone,
} from "../components/primitives";
import { day, relativeDay, stamp, humanise } from "../lib/format";

/**
 * The Lead's command view — the register.
 *
 * The structure is the argument. This platform's deepest property is that the
 * record is the truth: publication is an act with a name and a date, approval
 * binds to one exact version, withdrawal is a written fact rather than a
 * deletion, and nothing is ever edited away. A grid of stat tiles would hide
 * that; a register makes it the organising principle.
 *
 * Reading order is deliberately not "newest first". A Lead opens this to answer
 * one question — what needs me — so the block runs: what is late, what is
 * waiting on my decision, what I owe the client, then the record. The apparatus
 * in the margin carries the figures, so the block never has to.
 */
export function Command({ engagementId }: { engagementId: string }) {
  const { resource, reload } = useResource<CommandView>(
    () => api.get<CommandView>(`/api/engagements/${engagementId}/command`),
    [engagementId],
  );

  const [section, setSection] = useState("attention");
  const [postingUpdate, setPostingUpdate] = useState(false);
  /** The versionId whose decision panel is open, or null. One at a time. */
  const [deciding, setDeciding] = useState<string | null>(null);
  /**
   * The record starts closed.
   *
   * It is the longest block on the screen and the least urgent — the Desk is
   * read to answer "what needs me", and an append-only log of everything
   * that has ever happened pushed the three things that do need you off the
   * first screenful. Collapsed, it is still one click away and still says
   * how many entries it holds.
   */
  const [recordOpen, setRecordOpen] = useState(false);

  const railKeys = useMemo<RailKey[]>(() => {
    const data = resource.state === "ready" ? resource.data : null;
    const attention =
      (data?.needsAttention.overdueTasks.length ?? 0) +
      (data?.needsAttention.blockedTasks.length ?? 0) +
      (data?.needsAttention.unassignedTasks.length ?? 0);

    return [
      { id: "attention", key: "AT", label: `Needs attention (${attention})` },
      { id: "decide", key: "DE", label: `Awaiting your decision (${data?.awaitingDecision.length ?? 0})` },
      { id: "revisions", key: "RV", label: `Open revisions (${data?.openRevisions.length ?? 0})` },
      { id: "journey", key: "JN", label: "Milestones" },
      { id: "record", key: "RC", label: "The record" },
    ].map((k) => ({
      ...k,
      state: k.id === section ? ("live" as const) : ("rest" as const),
      onSelect: () => {
        setSection(k.id);
        // Jumping to a collapsed heading that then says nothing is worse
        // than not jumping, so selecting the record opens it.
        if (k.id === "record") setRecordOpen(true);
        document.getElementById(`region-${k.id}`)?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
          block: "start",
        });
      },
    }));
  }, [resource, section]);

  if (resource.state === "loading") {
    return (
      <AppShell>
        <ScreenHead title="Desk" />
        <SkeletonRows rows={7} />
      </AppShell>
    );
  }

  if (resource.state === "absent") {
    return (
      <AppShell>
        <ScreenHead title="Desk" />
        <AbsentNotice />
      </AppShell>
    );
  }

  if (resource.state === "failed") {
    return (
      <AppShell>
        <ScreenHead title="Desk" />
        <Failed
          message={resource.message}
          correlationId={resource.correlationId}
          onRetry={reload}
        />
      </AppShell>
    );
  }

  const view = resource.data;
  const { needsAttention: attention } = view;
  const attentionCount =
    attention.overdueTasks.length +
    attention.blockedTasks.length +
    attention.unassignedTasks.length;

  return (
    <AppShell engagement={view.engagement}>
      <ScreenHead
        title={view.engagement.name}
        sub={`${view.awaitingDecision.length} ${view.awaitingDecision.length === 1 ? "decision needs" : "decisions need"} your review.`}
      >
        <Mark tone={view.engagement.status === "active" ? "live" : "rest"}>
          {humanise(view.engagement.status)}
        </Mark>
        <Button
          tone="primary"
          onClick={() => setPostingUpdate((v) => !v)}
          aria-expanded={postingUpdate}
        >
          Add update <span aria-hidden>{postingUpdate ? "×" : "→"}</span>
        </Button>
      </ScreenHead>

      {postingUpdate && (
        <StatusUpdate
          engagementId={engagementId}
          calculated={view.progress.calculated}
          official={view.progress.official}
          onDone={() => {
            setPostingUpdate(false);
            reload();
          }}
          onCancel={() => setPostingUpdate(false)}
        />
      )}

      <RegisterLayout
        rail={<Rail keys={railKeys} />}
        margin={
          <Apparatus>
            <Progress
              calculated={view.progress.calculated}
              official={view.progress.official}
            />

            <div className="flex gap-7 xl:gap-8">
              <Figure
                value={attentionCount}
                label="needs attention"
                tone={attentionCount > 0 ? "danger" : "muted"}
              />
              <Figure
                value={view.awaitingDecision.length}
                label="awaiting you"
                tone={view.awaitingDecision.length > 0 ? "warning" : "muted"}
              />
            </div>

            {attention.overdueTasks.length > 0 && (
              <MarginNote title="Oldest overdue">
                {attention.overdueTasks[0]!.title} &mdash;{" "}
                {relativeDay(attention.overdueTasks[0]!.dueOn)}
                {attention.overdueTasks[0]!.assignee && (
                  <>, with {attention.overdueTasks[0]!.assignee}</>
                )}
              </MarginNote>
            )}

            {view.engagement.startedOn && (
              <MarginNote title="Running since">
                {day(view.engagement.startedOn)}
              </MarginNote>
            )}
          </Apparatus>
        }
      >
        {/* ------------------------------------------------- needs attention -- */}
        <div id="region-attention">
          <Region
            title="Issues to resolve"
            count={attentionCount}
            tone={attentionCount > 0 ? "attention" : "rest"}
          >
            {attentionCount === 0 ? (
              <Empty title="Nothing is late or unassigned">
                Every open task has an owner and a date that has not passed.
              </Empty>
            ) : (
              <>
                {attention.overdueTasks.map((task) => (
                  <Entry
                    key={task.id}
                    lead={task.title}
                    meta={
                      <>
                        Due {day(task.dueOn)} &middot; {relativeDay(task.dueOn)}
                        {task.assignee ? ` · ${task.assignee}` : " · unassigned"}
                      </>
                    }
                    right={<Mark tone="danger">Overdue</Mark>}
                  />
                ))}
                {attention.blockedTasks.map((task) => (
                  <Entry
                    key={task.id}
                    lead={task.title}
                    meta="Blocked by a dependency"
                    right={<Mark tone="warning">Blocked</Mark>}
                  />
                ))}
                {attention.unassignedTasks.map((task) => (
                  <Entry
                    key={task.id}
                    lead={task.title}
                    meta="No one is carrying this"
                    right={<Mark tone="warning">Unassigned</Mark>}
                  />
                ))}
              </>
            )}
          </Region>
        </div>

        {/* ------------------------------------------------------- decisions -- */}
        <div id="region-decide">
          <Region title="Awaiting your decision" count={view.awaitingDecision.length}>
            {view.awaitingDecision.length === 0 ? (
              <Empty title="No version is waiting on you">
                When someone submits work for your sign-off, it appears here
                with the exact version they sent.
              </Empty>
            ) : (
              view.awaitingDecision.map((item) => (
                <Entry
                  key={item.versionId}
                  lead={item.title}
                  meta={
                    <>
                      <span className="font-mono">v{item.versionNo}</span> &middot;{" "}
                      {item.submittedBy} &middot; {stamp(item.submittedAt)}
                    </>
                  }
                  right={
                    <>
                      <Mark tone="warning">In review</Mark>
                      <Button
                        tone="secondary"
                        onClick={() =>
                          setDeciding((open) =>
                            open === item.versionId ? null : item.versionId,
                          )
                        }
                        aria-expanded={deciding === item.versionId}
                      >
                        {deciding === item.versionId ? "Close" : "Review options"}
                      </Button>
                    </>
                  }
                >
                  {deciding === item.versionId && (
                    <Decide
                      deliverableId={item.deliverableId}
                      versionId={item.versionId}
                      onDone={() => {
                        setDeciding(null);
                        reload();
                      }}
                    />
                  )}
                </Entry>
              ))
            )}
          </Region>
        </div>

        {/* ------------------------------------------------------- revisions -- */}
        <div id="region-revisions">
          <Region title="Open revisions" count={view.openRevisions.length}>
            {view.openRevisions.length === 0 ? (
              <Empty title="No revisions outstanding">
                A change request stays here until a revised version is submitted.
              </Empty>
            ) : (
              view.openRevisions.map((rev) => (
                <Entry
                  key={rev.id}
                  lead={rev.deliverableTitle}
                  meta={
                    <>
                      Asked by {rev.requestedBy} &mdash; &ldquo;{rev.body}&rdquo;
                    </>
                  }
                  right={<Mark tone="danger">Revision requested</Mark>}
                />
              ))
            )}
          </Region>
        </div>

        {/* --------------------------------------------------------- journey -- */}
        <div id="region-journey">
          <Region title="Milestones" count={view.milestones.length}>
            {view.milestones.length === 0 ? (
              <Empty title="No milestones yet">
                Milestones are what your client sees as their journey. Without
                them, their portal can only show finished work.
              </Empty>
            ) : (
              view.milestones.map((m) => (
                <Entry
                  key={m.id}
                  tone={m.completedAt ? "spent" : "rest"}
                  lead={m.name}
                  meta={
                    m.completedAt
                      ? `Completed ${day(m.completedAt)}`
                      : m.dueOn
                        ? `Due ${day(m.dueOn)} · ${m.openTasks} open`
                        : `${m.openTasks} open`
                  }
                  right={
                    m.completedAt ? (
                      <Mark tone="success">Complete</Mark>
                    ) : m.overdue ? (
                      <Mark tone="danger">Overdue</Mark>
                    ) : (
                      <Mark>Open</Mark>
                    )
                  }
                />
              ))
            )}
          </Region>
        </div>

        {/* ---------------------------------------------------------- record -- */}
        <div id="region-record">
          <Region
            id="record"
            title="The record"
            count={view.recentActivity.length}
            open={recordOpen}
            onToggle={() => setRecordOpen((v) => !v)}
          >
            {view.recentActivity.length === 0 ? (
              <Empty title="Nothing recorded yet">
                As the engagement moves, every step is written here with who
                did it and when.
              </Empty>
            ) : (
              <Record events={view.recentActivity} />
            )}

            {/* Inside the Region, so it collapses with what it describes.
                Left outside, it was a paragraph of explanation dangling
                under a closed heading with nothing to explain. */}
            <p className="mt-3 max-w-[60ch] text-xs text-muted">
              A permanent record of everything that has happened on this
              engagement. Entries are never edited or removed.
            </p>
          </Region>
        </div>
      </RegisterLayout>
    </AppShell>
  );
}

/**
 * The record, made readable.
 *
 * Rendered against the real fixture it was twenty consecutive lines of
 * "System recorded Document", which is technically the truth and useless as a
 * surface. Three things fix it without hiding anything:
 *
 *   * **Runs collapse.** Six documents recorded by the same actor in the same
 *     minute is one fact — "6 documents recorded" — not six rows. The count is
 *     the information; the repetition was noise.
 *   * **Days are headed.** The record is read by when, so the day is a rule
 *     across the block rather than a repeated column.
 *   * **It is capped.** The ten most recent, then the rest on request. A Lead
 *     opens this to see what changed since they last looked.
 */
interface Activity {
  action: string;
  objectType: string;
  at: string;
  actor: string | null;
}

interface Run {
  key: string;
  actor: string | null;
  action: string;
  objectType: string;
  count: number;
  at: string;
}

function collapse(events: Activity[]): Run[] {
  const runs: Run[] = [];

  for (const event of events) {
    const minute = event.at.slice(0, 16);
    const previous = runs[runs.length - 1];

    if (
      previous &&
      previous.actor === event.actor &&
      previous.action === event.action &&
      previous.objectType === event.objectType &&
      previous.at.slice(0, 16) === minute
    ) {
      previous.count += 1;
      continue;
    }

    runs.push({
      key: `${event.at}-${event.objectType}-${runs.length}`,
      actor: event.actor,
      action: event.action,
      objectType: event.objectType,
      count: 1,
      at: event.at,
    });
  }

  return runs;
}

function Record({ events }: { events: Activity[] }) {
  const [showAll, setShowAll] = useState(false);
  const runs = useMemo(() => collapse(events), [events]);
  const shown = showAll ? runs : runs.slice(0, 10);

  let lastDay = "";

  return (
    <>
      {shown.map((run) => {
        const thisDay = day(run.at);
        const newDay = thisDay !== lastDay;
        lastDay = thisDay;

        const noun = humanise(run.objectType).toLowerCase();

        return (
          <div key={run.key}>
            {newDay && (
              <p className="label mt-5 border-t border-line pt-2.5 first:mt-0">
                {thisDay}
              </p>
            )}
            <Entry
              lead={
                <>
                  <span className="text-secondary">{run.actor ?? "The platform"}</span>{" "}
                  {verb(run.action)}{" "}
                  {run.count > 1 ? (
                    <>
                      <span data-numeric>{run.count}</span> {noun}s
                    </>
                  ) : (
                    <>{noun}</>
                  )}
                </>
              }
              meta={timeOnly(run.at)}
            />
          </div>
        );
      })}

      {runs.length > shown.length && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="register-entry w-full py-3 text-left text-xs text-muted transition-colors duration-150 hover:text-ink"
        >
          Show {runs.length - shown.length} earlier{" "}
          {runs.length - shown.length === 1 ? "entry" : "entries"}
        </button>
      )}
    </>
  );
}

/** Just the hour: the day is already set as a rule above the run. */
function timeOnly(at: string): string {
  const parsed = new Date(at);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** The record reads as sentences, so the raw verb is translated once. */
function verb(action: string): string {
  switch (action) {
    case "INSERT":
      return "recorded";
    case "UPDATE":
      return "changed";
    case "DELETE":
      return "removed";
    default:
      return action.toLowerCase();
  }
}

/** Status counts shown beside a deliverable, kept here so the tone map is shared. */
export function DeliverableMark({ status }: { status: string }) {
  return <Mark tone={deliverableTone(status)}>{humanise(status)}</Mark>;
}

/**
 * The Lead's status update.
 *
 * `postStatusUpdate` and `POST /api/engagements/:id/status` have both existed
 * since Slice 2. The button that should have called them was rendered with
 * **no onClick at all**, so "Add update" was decoration — you pressed it and
 * the platform did nothing, with no error to explain why.
 *
 * `officialProgress` is the part worth understanding. The calculated figure
 * counts closed rows; it is arithmetic and it does not know whether the
 * engagement is in trouble. The official figure is the Lead saying so. Left
 * blank, the calculation stands — which is why this is an optional field and
 * not a slider that defaults to something.
 */
function StatusUpdate({
  engagementId,
  calculated,
  official,
  onDone,
  onCancel,
}: {
  engagementId: string;
  calculated: number;
  official?: number | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [body, setBody] = useState("");
  const [progress, setProgress] = useState(
    official === null || official === undefined ? "" : String(official),
  );

  const post = useAction(async () => {
    const n = progress.trim() === "" ? undefined : Number(progress);
    await api.post(`/api/engagements/${engagementId}/status`, {
      body: body.trim(),
      ...(n === undefined ? {} : { officialProgress: n }),
    });
    setBody("");
    onDone();
  });

  const n = progress.trim() === "" ? null : Number(progress);
  const badNumber =
    n !== null && (!Number.isFinite(n) || n < 0 || n > 100);

  return (
    <section className="mt-6 border-y border-line py-6">
      <div className="grid max-w-3xl gap-5">
        <TextArea
          label="What is the state of this engagement"
          hint="This is saved with your name and the date and cannot be edited later."
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="max-w-xs">
          <Field
            label="Official progress"
            type="number"
            min={0}
            max={100}
            hint={`Optional. Leave blank and the calculated ${calculated}% stands.`}
            error={badNumber ? "A percentage between 0 and 100." : undefined}
            value={progress}
            onChange={(e) => setProgress(e.target.value)}
          />
        </div>
        <div className="flex gap-3">
          <Button
            tone="primary"
            loading={post.pending}
            disabled={!body.trim() || badNumber}
            onClick={() => void post.invoke()}
          >
            Post it
          </Button>
          <Button tone="quiet" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        {post.refusal && (
          <p className="border-l-2 border-danger pl-4 text-sm text-ink">
            {post.refusal.message}
          </p>
        )}
      </div>
    </section>
  );
}

const VERDICTS = [
  { value: "approved", label: "Approve", tone: "primary" as const },
  { value: "changes_requested", label: "Request changes", tone: "secondary" as const },
  { value: "rejected", label: "Reject", tone: "danger" as const },
];

/**
 * Deciding on one version.
 *
 * "Review options" was the second dead button — no onClick, so the one act
 * the screen is named for could not be performed from it. The route and
 * `decide()` were already there.
 *
 * The decision binds to `versionId`, not to the deliverable. That is the
 * platform's deepest rule and the reason this panel carries the version the
 * row named rather than looking up "the latest": approving a deliverable
 * would be approving whatever it becomes, which is not what anyone means.
 */
function Decide({
  deliverableId,
  versionId,
  onDone,
}: {
  deliverableId: string;
  versionId: string;
  onDone: () => void;
}) {
  const [comments, setComments] = useState("");
  const [note, setNote] = useState<string | null>(null);

  const decide = useAction(async (verdict: string) => {
    const result = await api.post<{ wasSelfApproval: boolean }>(
      `/api/deliverables/${deliverableId}/decision`,
      { versionId, decision: verdict, comments: comments.trim() || undefined },
    );
    // Not a refusal — the platform allows it and records it. Saying so is
    // the point: a self-approval that passes silently is the one nobody
    // notices until an audit.
    if (result?.wasSelfApproval) {
      setNote("Recorded — and noted as a self-approval, because you submitted this version yourself.");
      return;
    }
    onDone();
  });

  return (
    <div className="border-l-2 border-line pb-5 pl-4">
      <TextArea
        label="Comments"
        hint="Optional on approval. Say what needs doing if you are asking for changes."
        value={comments}
        onChange={(e) => setComments(e.target.value)}
      />
      <div className="mt-4 flex flex-wrap gap-3">
        {VERDICTS.map((v) => (
          <Button
            key={v.value}
            tone={v.tone}
            loading={decide.pending}
            onClick={() => void decide.invoke(v.value)}
          >
            {v.label}
          </Button>
        ))}
      </div>
      {note && (
        <p className="mt-4 border-l-2 border-line pl-4 text-sm text-secondary">
          {note}{" "}
          <button
            type="button"
            className="underline underline-offset-4 hover:text-ink"
            onClick={onDone}
          >
            Close
          </button>
        </p>
      )}
      {decide.refusal && (
        <p className="mt-4 border-l-2 border-danger pl-4 text-sm text-ink">
          {decide.refusal.message}
        </p>
      )}
    </div>
  );
}
